import { Link } from "react-router-dom";
import { ArrowRight, Languages, MapPin, HeartHandshake } from "lucide-react";
import { useSEO } from "@/lib/seo";
import { MaskedLine, FadeIn, Reveal, Chapter } from "@/components/Reveal";

const VALUES = [
    { num: "01", title: "Ownership", text: "We build your product like it's ours — because our own products taught us what cutting corners costs.", accent: "bg-blue-500" },
    { num: "02", title: "Craft", text: "Details compound. The extra hour on a screen, a sentence, a shadow — that's what people feel but can't name.", accent: "bg-purple-500" },
    { num: "03", title: "Candor", text: "If your idea doesn't need an app, we'll tell you. Honest advice is cheaper than an expensive mistake.", accent: "bg-rose-500" },
    { num: "04", title: "Momentum", text: "Small releases, real feedback, constant forward motion. We ship, learn, and improve — never stall.", accent: "bg-teal-500" },
];

export default function About() {
    useSEO(
        "About Us | Digital Circle — Calgary Digital Studio",
        "Meet Digital Circle: a Calgary-based team that builds its own products (LoadCircle, ServiceNext) and yours. Serving clients in English, Punjabi, and Hindi."
    );

    return (
        <main className="pt-32 md:pt-44" data-testid="about-page">
            <section className="mx-auto max-w-7xl px-5 md:px-8">
                <FadeIn>
                    <p className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.25em] text-slate-500">
                        <span className="ring-logo inline-block h-3.5 w-3.5" />
                        About
                    </p>
                </FadeIn>
                <h1 className="mt-6 max-w-4xl text-5xl font-black tracking-tighter text-slate-900 md:text-7xl" data-testid="about-headline">
                    <MaskedLine delay={0.1}>The circle behind</MaskedLine>
                    <MaskedLine delay={0.22}>
                        <span className="gradient-text">the products.</span>
                    </MaskedLine>
                </h1>
            </section>

            {/* ---------- STORY ---------- */}
            <section className="mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-28" data-testid="about-story">
                <Chapter index="01" label="Our story" />
                <div className="mt-10 grid items-center gap-12 lg:grid-cols-2">
                    <Reveal>
                        <div className="relative overflow-hidden rounded-[2rem]">
                            <img
                                src="https://images.unsplash.com/photo-1662099782192-f7ba7bf67236?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHxjYWxnYXJ5JTIwc2t5bGluZSUyMGRheWxpZ2h0fGVufDB8fHx8MTc4ODE1ODIyOHww&ixlib=rb-4.1.0&q=85"
                                alt="Calgary skyline with the Bow River"
                                className="h-[320px] w-full object-cover md:h-[440px]"
                                loading="lazy"
                                data-testid="about-calgary-image"
                            />
                            <div className="absolute bottom-4 left-4 flex items-center gap-2 rounded-full bg-white/85 px-4 py-2 text-xs font-semibold text-slate-900 backdrop-blur">
                                <MapPin className="h-3.5 w-3.5 text-rose-500" /> Calgary, Alberta
                            </div>
                        </div>
                    </Reveal>
                    <div>
                        <Reveal>
                            <h2 className="text-3xl font-black tracking-tighter text-slate-900 md:text-4xl">
                                We build yours like it's ours — because we've built ours.
                            </h2>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <div className="mt-6 space-y-5 text-base leading-relaxed text-slate-600 md:text-lg">
                                <p>
                                    Digital Circle started in Calgary with a simple frustration: too many agencies sell websites they've never had to live with. So we became the kind of studio we wanted to hire.
                                </p>
                                <p>
                                    Today we run two products of our own — <span className="font-semibold text-slate-900">LoadCircle</span>, a transportation management system used by real carriers, and <span className="font-semibold text-slate-900">ServiceNext</span>, a marketplace app for local service providers. Every lesson they teach us, we bring to your project.
                                </p>
                                <p>
                                    When you work with us, you get a team that thinks about your business the way we think about our own: what will this cost, what will it earn, and will it still matter in two years?
                                </p>
                            </div>
                        </Reveal>
                        <Reveal delay={0.15} className="mt-8 flex flex-wrap items-center gap-3">
                            <span className="flex items-center gap-2 rounded-full bg-slate-100 px-4 py-2 text-xs font-semibold text-slate-700">
                                <Languages className="h-3.5 w-3.5 text-blue-500" /> We serve clients in:
                            </span>
                            {["English", "Punjabi", "Hindi"].map((l) => (
                                <span key={l} data-testid={`language-${l.toLowerCase()}`} className="rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-900">
                                    {l}
                                </span>
                            ))}
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* ---------- VALUES ---------- */}
            <section className="bg-white py-16 md:py-28" data-testid="about-values">
                <div className="mx-auto max-w-7xl px-5 md:px-8">
                    <Chapter index="02" label="What we stand for" />
                    <Reveal className="mt-6">
                        <h2 className="max-w-2xl text-4xl font-black tracking-tighter text-slate-900 md:text-5xl">
                            Four values, no fine print.
                        </h2>
                    </Reveal>
                    <div className="mt-14 grid gap-x-10 gap-y-12 sm:grid-cols-2 lg:grid-cols-4">
                        {VALUES.map((v, i) => (
                            <Reveal key={v.num} delay={i * 0.08}>
                                <div className={`h-1 w-10 rounded-full ${v.accent}`} />
                                <p className="mt-5 text-xs font-semibold uppercase tracking-[0.25em] text-slate-400">{v.num}</p>
                                <h3 className="mt-3 text-xl font-bold tracking-tight text-slate-900">{v.title}</h3>
                                <p className="mt-3 text-sm leading-relaxed text-slate-600">{v.text}</p>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* ---------- TEAM ---------- */}
            <section className="mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-28" data-testid="about-team">
                <Chapter index="03" label="The team" />
                <div className="mt-10 grid items-center gap-12 lg:grid-cols-2">
                    <div className="order-2 lg:order-1">
                        <Reveal>
                            <h2 className="text-3xl font-black tracking-tighter text-slate-900 md:text-4xl">
                                Small on purpose. Senior on everything.
                            </h2>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <p className="mt-6 text-base leading-relaxed text-slate-600 md:text-lg">
                                No account managers relaying messages to anonymous contractors. The people you meet are the people who design, build, and ship your product — and answer the phone when you call.
                            </p>
                        </Reveal>
                        <Reveal delay={0.15} className="mt-8 flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-5">
                            <HeartHandshake className="h-8 w-8 shrink-0 text-rose-500" />
                            <p className="text-sm leading-relaxed text-slate-600">
                                Proudly serving Calgary's diverse business community — in English, Punjabi, and Hindi.
                            </p>
                        </Reveal>
                    </div>
                    <Reveal delay={0.1} className="order-1 lg:order-2">
                        <div className="overflow-hidden rounded-[2rem]">
                            <img
                                src="https://images.unsplash.com/photo-1559136555-9303baea8ebd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjcmVhdGl2ZSUyMGFnZW5jeSUyMG9mZmljZXxlbnwwfHx8fDE3ODg3MDY0Njd8MA&ixlib=rb-4.1.0&q=85"
                                alt="Digital Circle team collaborating in the studio"
                                className="h-[320px] w-full object-cover md:h-[440px]"
                                loading="lazy"
                                data-testid="about-team-image"
                            />
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* ---------- CTA ---------- */}
            <section className="mx-auto max-w-7xl px-5 pb-20 md:px-8 md:pb-28">
                <Reveal>
                    <div className="relative overflow-hidden rounded-[2.5rem] bg-slate-950 px-8 py-16 md:px-16 md:py-20">
                        <div className="conic-ring spin-slow absolute -right-32 -top-32 h-[380px] w-[380px] opacity-30 blur-2xl" aria-hidden="true" />
                        <div className="relative flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-center">
                            <h2 className="max-w-xl text-3xl font-black tracking-tighter text-white md:text-5xl">
                                Let's build your <span className="gradient-text">circle.</span>
                            </h2>
                            <Link
                                to="/contact"
                                data-testid="about-quote-button"
                                className="group inline-flex shrink-0 items-center gap-2 rounded-full bg-white px-8 py-4 text-sm font-semibold text-slate-900 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-2xl"
                            >
                                Get a Free Quote
                                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                            </Link>
                        </div>
                    </div>
                </Reveal>
            </section>
        </main>
    );
}
